import { getApiBaseUrl } from '../utils/apiConfig';
import getConfig from 'next/config';
import logger from '../utils/logger';
const { publicRuntimeConfig } = getConfig() || {};

// Types for billing API
export interface PlanPricing {
    id: string;
    name: string;
    price: number;
    period: number; // months
    currency_code: string;
    trial_period: number;
    trial_period_unit: string;
}

export interface CouponValidationRequest {
    coupon: string;
    items: Array<{
        id: string;
        type: 'subscription';
    }>;
}

export interface CouponValidationResponse {
    total: number;
    coupon_meta_data?: {
        reason?: string;
        extend_trial?: string;
    };
    coupon_display_text?: string; // Fallback reason text (legacy)
}

export interface PaymentIntentResponse {
    payment_intent: {
        id: string;
        status: string;
        amount: number;
        currency_code: string;
        expires_at: number;
        gateway_account_id: string;
        payment_method_type: string;
    };
}

export interface SubscriptionSubmitRequest {
    billingInfo: {
        firstName: string;
        lastName: string;
        email: string;
        company?: string;
    };
    paymentMethod: {
        type: 'card' | 'google_pay' | 'apple_pay';
        gatewayToken?: string;
        gatewayAccountId?: string;
        cardNumber?: string;
        expiryMonth?: string;
        expiryYear?: string;
        cvv?: string;
    };
    planId: string;
    couponCode?: string;
}

export interface SubscriptionResponse {
    success?: boolean;
    type?: 'success' | 'error';
    message?: {
        type?: string;
        text?: string;
    };
    subscription?: {
        id: string;
        status: string;
        plan_id: string;
        trial_end?: number;
    };
    error?: string;
}

// Plan feature for display
export interface PlanFeature {
    text: string;
    iconName: string;
    order: number;
}

// Plan asset badge
export interface PlanAsset {
    name: string;
    iconName: string;
    order: number;
}

// Plan strings for UI display
export interface PlanStringsJson {
    twoLiner: {
        line1: string;
        line2: string;
    };
    is4kAvailable: boolean;
    isPrime: boolean;
    assets: PlanAsset[];
    features: PlanFeature[];
    actionButton: string;
}

// Payment provider plan details
export interface PaymentProviderPlan {
    id: string;
    provider_plan_id: string;
    provider_plan_name: string;
    payment_cycle: 'monthly' | 'annually';
    trial_days: number;
    price: number;
    currency: string;
}

// Full plan data from API
export interface PlanData {
    uid: string;
    name: string; // Plan name for coupon matching: 'pro', 'plus', 'basic', etc.
    type: string;
    display_name: string;
    order: string;
    upgradable: boolean;
    trial_days: number;
    parameters: {
        name?: string;
        storage?: { quota: number };
        artcasts?: { public_quota: number };
        screens?: { quota: number };
        content?: { prime: boolean };
    };
    plan_strings_json: PlanStringsJson;
    payment_provider_user_plans: PaymentProviderPlan[];
}

// Generate public token using the same algorithm as the web app
function generatePublicToken(): string {
    const ENV = process.env.NODE_ENV || 'development';
    const appCodeName =
        (typeof navigator !== 'undefined' && navigator.appCodeName) ||
        'Mozilla';
    const appName =
        (typeof navigator !== 'undefined' && navigator.appName) || 'Netscape';
    const platform =
        (typeof navigator !== 'undefined' && navigator.platform) ||
        'Linux x86_64';

    let publicToken = ENV + appCodeName + appName + platform;

    publicToken = publicToken.replace(/M/g, () => {
        return Math.random().toString().substring(2, 6);
    });
    publicToken = publicToken.replace(/N/g, () => {
        return Math.random().toString().substring(2, 6);
    });
    publicToken = publicToken.replace(/d/g, () => {
        return Math.random().toString().substring(2, 6);
    });

    return publicToken;
}

function getPublicToken(): string {
    return (
        publicRuntimeConfig?.niioConfig?.app?.apiTokenPublic ||
        process.env.NEXT_PUBLIC_API_TOKEN_PUBLIC ||
        generatePublicToken()
    );
}

function getJwtToken(): string {
    if (typeof window !== 'undefined') {
        const userJwtToken = localStorage.getItem('jwtToken');
        if (userJwtToken) {
            return userJwtToken;
        }
    }
    return (
        publicRuntimeConfig?.niioConfig?.app?.apiToken ||
        process.env.NEXT_PUBLIC_API_TOKEN ||
        ''
    );
}

function getApiUrl(): string {
    return getApiBaseUrl();
}

function getHeaders(): HeadersInit {
    return {
        'Content-Type': 'application/json',
        'X-MyApiTokenHeaderPublic': getPublicToken(),
        'X-MyApiTokenHeader': getJwtToken(),
    };
}

// Cache for plans data (keyed by provider)
const plansCacheMap: Map<string, PlanData[]> = new Map();
const plansCachePromiseMap: Map<string, Promise<PlanData[]>> = new Map();
const plansCacheTimeMap: Map<string, number> = new Map();
const PLANS_CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours

/**
 * Billing API Service
 * Handles all billing-related API calls for the native payment form
 */
export const billingApi = {
    /**
     * Pre-fetch and cache plans data
     * Call this on app load to warm the cache
     */
    async prefetchPlans(): Promise<void> {
        try {
            logger.debug('[BILLING-API] Pre-fetching plans...');
            await this.getPlansWithDetails();
            logger.debug('[BILLING-API] Plans pre-fetched successfully');
        } catch (error) {
            console.error('[BILLING-API] Failed to pre-fetch plans:', error);
            // Don't throw - pre-fetching is optional
        }
    },

    /**
     * Clear the plans cache (useful after subscription changes)
     */
    clearPlansCache(): void {
        logger.debug('[BILLING-API] Clearing plans cache');
        plansCacheMap.clear();
        plansCachePromiseMap.clear();
        plansCacheTimeMap.clear();
    },

    /**
     * Get subscription plans with pricing (simple format)
     */
    async getPlans(): Promise<PlanPricing[]> {
        const response = await fetch(`${getApiUrl()}/billing/getPlans`, {
            method: 'POST',
            headers: getHeaders(),
            credentials: 'include',
            body: JSON.stringify({}),
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch plans: ${response.status}`);
        }

        const data = await response.json();
        return data.plans || [];
    },

    /**
     * Get full plan data with features and strings for display
     * Returns all plans with plan_strings_json for UI rendering
     * Uses caching to avoid repeated API calls (per provider)
     */
    async getPlansWithDetails(
        provider: string = 'charge_bee'
    ): Promise<PlanData[]> {
        const now = Date.now();
        const cachedPlans = plansCacheMap.get(provider);
        const cacheTime = plansCacheTimeMap.get(provider) || 0;

        // Return cached data if valid
        if (cachedPlans && cacheTime && now - cacheTime < PLANS_CACHE_TTL) {
            logger.debug(
                `[BILLING-API] Returning cached plans for provider: ${provider}`
            );
            return cachedPlans;
        }

        // If there's already a request in flight for this provider, wait for it
        const existingPromise = plansCachePromiseMap.get(provider);
        if (existingPromise) {
            logger.debug(
                `[BILLING-API] Waiting for in-flight plans request for provider: ${provider}`
            );
            return existingPromise;
        }

        logger.debug(
            `[BILLING-API] Fetching plans with details from API for provider: ${provider}`
        );

        // Create and store the promise to prevent duplicate requests
        const fetchPromise = (async () => {
            try {
                const response = await fetch(
                    `${getApiUrl()}/billing/getPlans`,
                    {
                        method: 'POST',
                        headers: getHeaders(),
                        credentials: 'include',
                        body: JSON.stringify({
                            providerName: provider,
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch plans: ${response.status}`
                    );
                }

                const data = await response.json();
                const plans = data.plans || [];
                logger.debug(
                    `[BILLING-API] Plans fetched for ${provider}:`,
                    plans.length
                );

                // Update cache for this provider
                plansCacheMap.set(provider, plans);
                plansCacheTimeMap.set(provider, Date.now());

                return plans;
            } finally {
                // Clear the promise so future calls can retry
                plansCachePromiseMap.delete(provider);
            }
        })();

        plansCachePromiseMap.set(provider, fetchPromise);
        return fetchPromise;
    },

    /**
     * Get plan data by provider plan ID (e.g., 'Basic-USD-Monthly')
     * @param providerPlanId - The provider-specific plan ID
     * @param provider - The payment provider ('apple' or 'charge_bee')
     */
    async getPlanByProviderId(
        providerPlanId: string,
        provider: string = 'charge_bee'
    ): Promise<PlanData | null> {
        const plans = await this.getPlansWithDetails(provider);

        // Find the plan that contains this provider plan ID
        const plan = plans.find((p) =>
            p.payment_provider_user_plans?.some(
                (pp) => pp.provider_plan_id === providerPlanId
            )
        );

        if (plan) {
            logger.debug(
                `[BILLING-API] Found plan for provider ID (${provider}):`,
                providerPlanId,
                plan.display_name
            );
        } else {
            logger.debug(
                `[BILLING-API] No plan found for provider ID (${provider}):`,
                providerPlanId
            );
        }

        return plan || null;
    },

    /**
     * Validate a coupon code and get discount info
     */
    async validateCoupon(
        couponCode: string,
        planId: string
    ): Promise<CouponValidationResponse> {
        logger.debug('[BILLING-API] Validating coupon:', {
            couponCode,
            planId,
        });

        const response = await fetch(`${getApiUrl()}/billing/validateCoupon`, {
            method: 'POST',
            headers: getHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                coupon: couponCode,
                items: [
                    {
                        id: planId,
                        type: 'subscription',
                    },
                ],
            }),
        });

        if (!response.ok) {
            const errorText = await response
                .text()
                .catch(() => 'Unknown error');
            console.error('[BILLING-API] Coupon validation failed:', errorText);

            // Try to extract user-friendly error message from response
            let userMessage = 'Invalid coupon code';
            try {
                const errorData = JSON.parse(errorText);
                // Check for message.text (API format)
                if (errorData.message?.text) {
                    userMessage = errorData.message.text;
                } else if (typeof errorData.message === 'string') {
                    userMessage = errorData.message;
                } else if (errorData.text) {
                    userMessage = errorData.text;
                }
            } catch {
                // If not JSON, use generic message
            }

            throw new Error(userMessage);
        }

        const data = await response.json();
        logger.debug('[BILLING-API] Coupon validation response:', data);
        // API returns { data: { total, coupon_meta_data, ... } } - unwrap if needed
        return data.data || data;
    },

    /**
     * Create a payment intent for digital wallets (Google Pay, Apple Pay)
     */
    async createPaymentIntent(
        amount: number,
        currencyCode: string,
        paymentMethodType: 'google_pay' | 'apple_pay',
        planId: string,
        customerId?: string
    ): Promise<PaymentIntentResponse> {
        logger.debug('[BILLING-API] Creating payment intent:', {
            amount,
            currencyCode,
            paymentMethodType,
            planId,
        });

        const response = await fetch(
            `${getApiUrl()}/billing/createPaymentIntent`,
            {
                method: 'POST',
                headers: getHeaders(),
                credentials: 'include',
                body: JSON.stringify({
                    amount,
                    currency_code: currencyCode,
                    payment_method_type: paymentMethodType,
                    plan_id: planId,
                    customer_id: customerId,
                }),
            }
        );

        if (!response.ok) {
            const errorText = await response
                .text()
                .catch(() => 'Unknown error');
            console.error(
                '[BILLING-API] Payment intent creation failed:',
                errorText
            );
            throw new Error(
                `Failed to create payment intent: ${response.status}`
            );
        }

        // Check content type to ensure we're getting JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            const text = await response.text().catch(() => '');
            console.error(
                '[BILLING-API] Payment intent response is not JSON:',
                text.substring(0, 200)
            );
            throw new Error(
                `Server returned non-JSON response for payment intent`
            );
        }

        const data = await response.json();
        logger.debug('[BILLING-API] Payment intent created:', data);
        return data;
    },

    /**
     * Submit billing info and create subscription
     * Converts to flat format expected by the backend API
     */
    async submitBillingInfoAndSubscribe(
        request: SubscriptionSubmitRequest
    ): Promise<SubscriptionResponse> {
        logger.debug('[BILLING-API] Submitting subscription:', {
            planId: request.planId,
            paymentType: request.paymentMethod.type,
            hasCoupon: !!request.couponCode,
        });

        // Convert to flat format expected by the backend
        // Backend expects: firstName, lastName, email, company, itemPriceId, gatewayToken, gatewayAccountId, etc.
        const flattenedRequest: Record<string, unknown> = {
            // User info (required for new users)
            firstName: request.billingInfo.firstName,
            lastName: request.billingInfo.lastName,
            email: request.billingInfo.email,
            company: request.billingInfo.company || '',

            // Plan info
            itemPriceId: request.planId,

            // Coupon (if any) - backend expects string, not null
            coupon: request.couponCode || '',
        };

        // Add payment method data based on type
        if (
            request.paymentMethod.type === 'google_pay' ||
            request.paymentMethod.type === 'apple_pay'
        ) {
            // Digital wallet payment - backend checks for gatewayToken
            flattenedRequest.gatewayToken = request.paymentMethod.gatewayToken;
            flattenedRequest.gatewayAccountId =
                request.paymentMethod.gatewayAccountId || '';
            flattenedRequest.paymentMethod = 'digital_wallet';
        } else if (request.paymentMethod.type === 'card') {
            // Card payment
            flattenedRequest.cardNumber = request.paymentMethod.cardNumber;
            flattenedRequest.cvc = request.paymentMethod.cvv;
            flattenedRequest.expirationMonth =
                request.paymentMethod.expiryMonth;
            flattenedRequest.expirationYear = request.paymentMethod.expiryYear;
        }

        logger.debug('[BILLING-API] Flattened request:', flattenedRequest);

        const response = await fetch(
            `${getApiUrl()}/billing/submitBillingInfoAndSubscribe`,
            {
                method: 'POST',
                headers: getHeaders(),
                credentials: 'include',
                body: JSON.stringify(flattenedRequest),
            }
        );

        if (!response.ok) {
            const errorText = await response
                .text()
                .catch(() => 'Unknown error');
            console.error(
                '[BILLING-API] Subscription submission failed:',
                errorText
            );

            // Try to parse error message from response
            try {
                const errorData = JSON.parse(errorText);
                // Extract error message - handle nested structures
                let errorMessage: string;
                if (typeof errorData.error === 'string') {
                    errorMessage = errorData.error;
                } else if (typeof errorData.message === 'string') {
                    errorMessage = errorData.message;
                } else if (errorData.message?.text) {
                    errorMessage = errorData.message.text;
                } else if (typeof errorData === 'object') {
                    // Try to stringify the error for debugging
                    errorMessage = JSON.stringify(errorData);
                } else {
                    errorMessage = `Subscription failed: ${response.status}`;
                }
                throw new Error(errorMessage);
            } catch (parseError) {
                // If it's already an Error from the JSON parsing above (successful parse), rethrow it
                if (
                    parseError instanceof Error &&
                    !parseError.message.includes('Unexpected token')
                ) {
                    throw parseError;
                }
                // Otherwise it's a JSON parse error, throw generic message
                throw new Error(`Subscription failed: ${response.status}`);
            }
        }

        const data = await response.json();
        logger.debug('[BILLING-API] Subscription created:', data);
        return data;
    },

    /**
     * Get user's saved payment sources (for returning users)
     */
    async getUserPaymentSources(): Promise<
        Array<{
            id: string;
            type: string;
            last4: string;
            brand: string;
            expiry_month: number;
            expiry_year: number;
        }>
    > {
        const response = await fetch(
            `${getApiUrl()}/billing/getUserPaymentSources`,
            {
                method: 'POST',
                headers: getHeaders(),
                credentials: 'include',
                body: JSON.stringify({}),
            }
        );

        if (!response.ok) {
            // No saved cards is not an error
            if (response.status === 404) {
                return [];
            }
            throw new Error(
                `Failed to fetch payment sources: ${response.status}`
            );
        }

        const data = await response.json();
        return data.payment_sources || [];
    },
};

export default billingApi;
